import {
  Box,
  Divider,
  HStack,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  type Expense,
  ExpenseInvoiceStatusEnum,
  ExpenseOrderByColumnEnum,
  ExpenseStatusEnum,
  ModelLockLockableTypeEnum,
  type SortOrder,
  useMarkExpenseAsPaidBulkMutation,
} from "@src/__generated__/graphql";
import { AbraFlexiExportedIcon } from "@src/components/modules/common/exports/abraflexi/AbraFlexiExportedIcon";
import { DocumentExportIndicator } from "@src/components/modules/common/exports/document-export-indicator";
import { XeroExportedIcon } from "@src/components/modules/common/exports/xero/Icon";
import {
  PaidAtCell,
  PaidCell,
} from "@src/components/modules/invoices/shared/columns";
import { LockableColumn } from "@src/components/ui-kit/DataTable/columns/lockable-column";
import { Icon } from "@src/components/ui-kit/Icon";
import type {
  BulkAction,
  BulkSubAction,
} from "@src/components/widgets/bulk-actions/types";
import { PreviewAndDownloadCell } from "@src/components/widgets/TableCell";
import { appStore } from "@src/stores/AppStore";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { now, toApiDate } from "@src/utils/dates";
import { currency, dateTime, formatDate } from "@src/utils/formatters";
import { DatePickerModal } from "@src/utils/forms/DatePickerModal";
import type { DateRange as Range } from "@src/utils/types";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useContext, useMemo } from "react";
import type { ExpenseModel } from "../models";
import { PageExpensesCtx } from "../PageExpenses";
import { ExpenseStatusBadge } from "./ExpenseStatusBadge";
import { PopoverRowActions } from "./PopoverRowActions";

export type TData = {
  id: string;
  type: { type: ExpenseModel["type"]; isDraft: boolean };
  invoice_status: ExpenseModel["invoice_status"];
  status: ExpenseModel["status"];
  contractor: ExpenseModel["contractor"];
  projects: ExpenseModel["projects"];
  variableSymbol: ExpenseModel["variable_symbol"];
  iban: ExpenseModel["iban"];
  amount: ExpenseModel["amount"];
  total_amount_without_vat_in_currency: ExpenseModel["total_amount_without_vat_in_currency"];
  total_amount_with_vat_in_currency: ExpenseModel["total_amount_with_vat_in_currency"];
  currency: ExpenseModel["currency"];
  dateOfSupply: ExpenseModel["date_of_supply"];
  dueDate: ExpenseModel["due_date"];
  paidAt: ExpenseModel["paid_at"];
  abraflexi_exported: ExpenseModel["abraflexi_exported"];
  xero_exported: ExpenseModel["xero_exported"];
  hasMasterAgreement: ExpenseModel["hasMasterAgreement"];
  documentExports: ExpenseModel["documentExports"];
  userCreatedDate: ExpenseModel["userCreatedDate"];
  expense: string;
  actions: string;
  _model: ExpenseModel;
  expense_no: ExpenseModel["expense_no"];
  exchange_rate: ExpenseModel["exchange_rate"];
  responsibleUsers: string[];
  workspaceCompanyName: ExpenseModel["workspaceCompanyName"];
  accountMoves: ExpenseModel["accountMoves"];
  paid: ExpenseModel["paid"];
  projectTimeBilling: ExpenseModel["projectTimeBilling"];
  modelLock: ExpenseModel["modelLock"];
  modelLockToggle: (locked: boolean) => void;
  note: ExpenseModel["note"];
  internalNote: ExpenseModel["internal_note"];
};

type ExpenseOrderByClause = {
  column: ExpenseOrderByColumnEnum;
  order: SortOrder;
};

export const getColorByStatus = (status: Expense["invoice_status"]): string => {
  switch (status) {
    case ExpenseInvoiceStatusEnum.Invoiced:
      return "orange.500";
    case ExpenseInvoiceStatusEnum.InvoicedAndPaid:
      return "green.500";
    case ExpenseInvoiceStatusEnum.NotInvoiced:
      return "red.500";
    case ExpenseInvoiceStatusEnum.ProBono:
      return "blue.500";
    case ExpenseInvoiceStatusEnum.NonInvoiceable:
      return "grey.500";
    default:
      return "white";
  }
};

export const getStatusMessage = (status: Expense["invoice_status"]) => {
  switch (status) {
    case ExpenseInvoiceStatusEnum.Invoiced:
      return t`Expense is not paid by Client`;
    case ExpenseInvoiceStatusEnum.InvoicedAndPaid:
      return t`Expense was paid by Client`;
    case ExpenseInvoiceStatusEnum.NotInvoiced:
      return t`Expense is not covered by Outgoing invoices`;
    case ExpenseInvoiceStatusEnum.ProBono:
      return t`Expense will not be paid - Non-billable`;
    case ExpenseInvoiceStatusEnum.NonInvoiceable:
      return t`Expense will not be paid`;
    default:
      return "";
  }
};

export const TableExpenses = observer(function TableExpenses() {
  const store = useContext(PageExpensesCtx);
  const state = useLocalObservable(() => ({
    markAsPaidModalOpen: false,
    selectedDate: {
      start: now(),
      end: now(),
    } as Range,
    selectedIdsForMarkAsPaid: [] as string[],
    openMarkAsPaid(ids: string[]): void {
      this.selectedIdsForMarkAsPaid = ids;
      this.markAsPaidModalOpen = true;
    },
    closeMarkAsPaid(): void {
      this.markAsPaidModalOpen = false;
    },
    setSelectedDate(date: Range): void {
      this.selectedDate = date;
    },
  }));

  const [markExpenseAsPaidBulk, { loading: markAsPaidLoading }] =
    useMarkExpenseAsPaidBulkMutation({
      ...onError(),
      onCompleted() {
        state.closeMarkAsPaid();
        store.refetch?.();
      },
    });

  const data: TData[] = useMemo(
    () =>
      store.expenses.map((expense) => {
        const responsibleNames = new Set<string>();

        for (const item of expense.expenseItems) {
          if (item.approved) continue;

          const user = store.userSimpleMap.find(
            ({ id }) => id === item.mandatory_id,
          );
          if (!user) continue;

          responsibleNames.add(user.full_name);
        }

        return {
          id: expense.id,
          abraflexi_exported: expense.abraflexi_exported,
          xero_exported: expense.xero_exported,
          type: { type: expense.type, isDraft: expense.is_draft },
          contractor: expense.contractor,
          projects: expense.projects,
          variableSymbol: expense.variable_symbol,
          iban: expense.iban,
          amount: expense.amount,
          total_amount_without_vat_in_currency:
            expense.total_amount_without_vat_in_currency,
          total_amount_with_vat_in_currency:
            expense.total_amount_with_vat_in_currency,
          currency: expense.currency,
          dateOfSupply: expense.date_of_supply,
          hasMasterAgreement: expense.hasMasterAgreement,
          documentExports: expense.documentExports,
          userCreatedDate: expense.userCreatedDate,
          dueDate: expense.due_date,
          paidAt: expense.paid_at,
          status: expense.status,
          invoice_status: expense.invoice_status,
          expense: "",
          actions: "",
          _model: expense,
          expense_no: expense.expense_no,
          exchange_rate: expense.exchange_rate,
          responsibleUsers: Array.from(responsibleNames),
          workspaceCompanyName: expense.workspaceCompanyName,
          accountMoves: expense.accountMoves,
          paid: expense.paid,
          projectTimeBilling: expense.projectTimeBilling,
          modelLock: expense.modelLock,
          modelLockToggle: (locked: boolean) =>
            locked
              ? store.unlockExpense(expense.id)
              : store.lockExpense(expense.id),
          note: expense.note,
          internalNote: expense.internal_note,
        };
      }),
    [store.expenses, store.userSimpleMap],
  );

  const orderedBy = store.orderBy.clauses[0] as
    | ExpenseOrderByClause
    | undefined;

  const columns = useMemo<
    BodyColumnDef<TData, ExpenseOrderByColumnEnum>[]
  >(() => {
    const allColumns: BodyColumnDef<TData, ExpenseOrderByColumnEnum>[] = [
      {
        id: "id",
        size: 80,
        meta: { label: t`ID` },
        header: t`ID`,
        orderBy: ExpenseOrderByColumnEnum.Id,
        cell: ({ row }) => {
          const expense = row.original;

          return (
            <div className="flex items-center">
              <Link
                href={{
                  pathname: "/expenses/edit",
                  query: { id: expense.id },
                }}
              >
                <div className="mr-3">{expense.id}</div>
              </Link>
              <Tooltip
                label={
                  <div className="flex px-3 py-4 leading-relaxed text-lg text-center">
                    <p>{getStatusMessage(expense.invoice_status)}</p>
                  </div>
                }
              >
                <Box ml="2">
                  <Icon
                    name="currency-euro-circle"
                    color={getColorByStatus(expense.invoice_status)}
                  />
                </Box>
              </Tooltip>
              {expense.abraflexi_exported && <AbraFlexiExportedIcon />}
              {expense.xero_exported && <XeroExportedIcon />}
              <DocumentExportIndicator
                documentExports={expense.documentExports}
              />
            </div>
          );
        },
      },
      {
        id: "expense-no",
        meta: { label: t`Expense no` },
        header: t`Expense no`,
        orderBy: ExpenseOrderByColumnEnum.ExpenseNoRaw,
        cell: ({ row }) => (
          <Link
            href={{
              pathname: "/expenses/edit",
              query: { id: row.original.id },
            }}
          >
            {row.original.expense_no}
          </Link>
        ),
      },
      {
        id: "type",
        meta: { label: t`Type` },
        header: t`Type`,
        orderBy: ExpenseOrderByColumnEnum.Type,
        cell: ({ row }) => row.original.type.type,
      },
      {
        id: "status",
        meta: { label: t`Status` },
        header: t`Status`,
        orderBy: ExpenseOrderByColumnEnum.Status,
        cell: ({ row }) => <ExpenseStatusBadge status={row.original.status} />,
      },
      {
        id: "responsible",
        meta: { label: t`Responsible` },
        header: () => <Trans>Responsible</Trans>,
        cell: ({ row }) => {
          const { responsibleUsers, status } = row.original;

          if (
            status !== ExpenseStatusEnum.ForApproval &&
            status !== ExpenseStatusEnum.ForFinancialApproval
          ) {
            return null;
          }

          return (
            <Text whiteSpace="normal" wordBreak="break-word">
              {responsibleUsers.join(", ")}
            </Text>
          );
        },
      },
      {
        id: "contractor",
        meta: { label: t`Contractor` },
        header: t`Contractor`,
        orderBy: ExpenseOrderByColumnEnum.Contractor,
        cell: ({ row }) => {
          const expense = row.original;

          if (!expense.contractor) {
            return null;
          }

          return (
            <Box>
              <Link
                href={{
                  pathname: "/contractors/detail",
                  query: { id: expense.contractor.id },
                }}
              >
                {expense.contractor.legal_name}
              </Link>
              {!expense.hasMasterAgreement && (
                <Tooltip
                  hasArrow
                  label={
                    <Trans>
                      This is a regular invoice and the contractor does not have
                      any active Master Agreement
                    </Trans>
                  }
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="alert-circle" color="red" ml="1" />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        id: "projects",
        size: 160,
        meta: { label: t`Project/s` },
        header: () => <Trans>Project/s</Trans>,
        cell: ({ row }) => (
          <Box>
            {row.original.projects.map((project) => (
              <React.Fragment key={project.id}>
                <Tooltip
                  hasArrow
                  label={`${project.code} - ${project.title.replace(" ", "-")}`}
                  placement="top-start"
                >
                  <Box>
                    <Link
                      key={project.id}
                      href={{
                        pathname: "/projects/detail",
                        query: { id: project.id },
                      }}
                    >
                      <div>{project.code}</div>
                      <div>{project.title}</div>
                    </Link>
                  </Box>
                </Tooltip>
                <br />
              </React.Fragment>
            ))}
          </Box>
        ),
      },
      {
        id: "legal-entity",
        meta: { label: t`Legal entity` },
        header: () => <Trans>Legal entity</Trans>,
        cell: ({ row }) => (
          <Text>{row.original.workspaceCompanyName ?? ""}</Text>
        ),
      },
      {
        id: "variableSymbol",
        meta: { label: t`Var. symbol` },
        header: t`Var. symbol`,
        accessorKey: "variableSymbol",
        orderBy: ExpenseOrderByColumnEnum.VariableSymbol,
      },
      {
        id: "iban",
        meta: { label: t`IBAN` },
        header: t`IBAN`,
        orderBy: ExpenseOrderByColumnEnum.Iban,
        cell: ({ row }) => {
          const iban = row.original.iban;

          return (
            iban && (
              <Tooltip label={<p>{iban}</p>}>
                <div>{iban.slice(0, 5)}...</div>
              </Tooltip>
            )
          );
        },
      },
      {
        id: "created-at",
        meta: { label: t`Created at` },
        header: t`Created at`,
        orderBy: ExpenseOrderByColumnEnum.UserCreatedDate,
        cell: ({ row }) => formatDate(row.original.userCreatedDate),
      },
      {
        id: "amount",
        meta: { label: t`Amount` },
        header: t`Amount`,
        orderBy: ExpenseOrderByColumnEnum.Amount,
        cell: ({ row }) =>
          currency.formatByCurrency(
            row.original.total_amount_without_vat_in_currency,
            row.original.currency,
          ),
      },
      {
        id: "amount-with-vat",
        meta: { label: t`Amount (with VAT)` },
        header: () => <Trans>Amount (with VAT)</Trans>,
        cell: ({ row }) =>
          currency.formatByCurrency(
            row.original.total_amount_with_vat_in_currency,
            row.original.currency,
          ),
      },
      {
        id: "dateOfSupply",
        meta: { label: t`Date of supply` },
        header: t`Date of supply`,
        orderBy: ExpenseOrderByColumnEnum.DateOfSupply,
        cell: ({ row }) =>
          row.original.dateOfSupply
            ? dateTime.format(row.original.dateOfSupply)
            : "",
      },
      {
        id: "dueDate",
        meta: { label: t`Due date` },
        header: t`Due date`,
        orderBy: ExpenseOrderByColumnEnum.DueDate,
        cell: ({ row }) =>
          row.original.dueDate ? dateTime.format(row.original.dueDate) : "",
      },
      {
        id: "paid",
        meta: { label: t`Paid` },
        header: () => <Trans>Paid</Trans>,
        cell: ({ row }) => (
          <PaidCell
            paid={row.original.paid ? Math.abs(row.original.paid) : null}
            currency={row.original.currency}
          />
        ),
      },
      {
        id: "paidAt",
        meta: { label: t`Paid at` },
        header: t`Paid at`,
        orderBy: ExpenseOrderByColumnEnum.PaidAt,
        cell: ({ row }) => (
          <PaidAtCell
            paidAt={row.original.paidAt}
            hasPayment={!!row.original.paid && row.original.paid !== 0}
          />
        ),
      },
      {
        id: "expense",
        meta: { label: t`Expense` },
        header: () => <Trans>Expense</Trans>,
        cell: ({ row }) => (
          <PreviewAndDownloadCell
            value={row.original._model.expense_documents}
          />
        ),
      },
      {
        id: "modelLock",
        header: () => "",
        meta: { label: t`Lock` },
        size: 40,
        cell: ({ row }) => (
          <LockableColumn
            modelLock={row.original.modelLock}
            canToggle={can("expense_modelLock")}
            onClick={row.original.modelLockToggle}
            isLoading={appStore.modelLock.isLoading(
              row.original.id,
              ModelLockLockableTypeEnum.Expense,
            )}
          />
        ),
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: () => <Trans>Note</Trans>,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.note} />
        ),
      },
      {
        id: "internalNote",
        meta: { label: t`Internal note` },
        header: () => <Trans>Internal note</Trans>,
        cell: ({ row }) => (
          <TruncatedTextWithTooltip text={row.original.internalNote} />
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => <Trans>Actions</Trans>,
        cell: ({ row }) => (
          <PopoverRowActions
            model={row.original}
            id={row.original.id}
            paid={Boolean(row.original.paidAt)}
          />
        ),
      },
    ];

    const columnsWithVisibilityRules = allColumns.filter((column) => {
      if (column.id !== "legal-entity") {
        return true;
      }

      return appStore.workspaceStore.hasMultipleCompanies;
    });

    if (!store.asExternalModule) {
      return columnsWithVisibilityRules;
    }

    return columnsWithVisibilityRules.filter(
      (column) => !store.hideColsInExternalModule.includes(String(column.id)),
    );
  }, [store]);

  const bulkActions = useMemo<BulkAction[]>(() => {
    const bulkActions: BulkAction[] = [
      {
        id: "expense-bulk-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () => store.bulkActionsLoading || store.isBulkDeleting,
        subActions: [
          ...(can("expense_markAsPaid")
            ? [
                {
                  id: "mark-as-paid",
                  label: () => t`Mark as paid`,
                  callback: (selectedIds) => {
                    state.openMarkAsPaid(Array.from(selectedIds));
                  },
                } satisfies BulkSubAction,
              ]
            : []),
          {
            id: "download-attachments",
            label: () => t`Download attachments`,
            callback: (selectedIds) => {
              store.downloadAttachmentsBulkAction(Array.from(selectedIds));
            },
          },
        ],
      },
    ];

    if (can("expense_modelLock")) {
      bulkActions.push(
        {
          id: "lock",
          label: () => t`Lock`,
          icon: "lock-01",
          isLoading: () => appStore.modelLock.isBulkLocking,
          callback: (selectedIds) => {
            store.bulkLockExpense(Array.from(selectedIds));
          },
        },
        {
          id: "unlock",
          label: () => t`Unlock`,
          icon: "lock-unlocked-01",
          isLoading: () => appStore.modelLock.isBulkUnlocking,
          callback: (selectedIds) => {
            store.bulkUnlockExpenses(Array.from(selectedIds));
          },
        },
      );
    }

    bulkActions.push({
      id: "delete",
      label: () => t`Delete`,
      icon: "trash-03",
      iconColorScheme: "red",
      callback: (selectedIds) => {
        store.bulkDeleteExpenses(Array.from(selectedIds));
      },
    });

    return bulkActions;
  }, [state, store]);

  return (
    <React.Fragment>
      <DataTable.Body
        tableKey={store.tableKey}
        data={data}
        columns={columns}
        orderedBy={orderedBy}
        onOrderByChange={(clause) => {
          const { column, order } = clause;

          if (!order) {
            store.orderBy.setClauses([]);
            return;
          }

          store.orderBy.setClauses([{ column, order }]);
        }}
        bulkActions={bulkActions}
        bulkActionsInfoBlock={
          <HStack
            fontSize="xs"
            divider={<Divider h="6" orientation="vertical" />}
            spacing="4"
          >
            <VStack align="end" spacing="0">
              <Text fontSize="inherit" fontWeight="semibold">
                <Trans>Total amount</Trans>
              </Text>

              <Skeleton minW="40px" isLoaded={!store.isSelectedSummaryLoading}>
                <Text fontSize="inherit" textAlign="end">
                  {currency.formatByWorkspace(
                    store.selectedSummary?.total_amount ?? 0,
                  )}
                </Text>
              </Skeleton>
            </VStack>

            <VStack align="end" spacing="0">
              <Text fontSize="inherit" fontWeight="semibold">
                <Trans>Total amount with VAT</Trans>
              </Text>

              <Skeleton minW="40px" isLoaded={!store.isSelectedSummaryLoading}>
                <Text fontSize="inherit" textAlign="end">
                  {currency.formatByWorkspace(
                    store.selectedSummary?.total_amount_with_vat ?? 0,
                  )}
                </Text>
              </Skeleton>
            </VStack>
          </HStack>
        }
        enableRowSelection={store.selectable}
        selected={store.selectedExpensesIds}
        onSelect={store.setSelectedIds}
        allIds={store.allExpensesIds}
      />

      <DatePickerModal
        isOpen={state.markAsPaidModalOpen}
        onClose={state.closeMarkAsPaid}
        setSelectedDate={state.setSelectedDate}
        primaryAction={{
          title: t`Mark as paid`,
          action: () => {
            if (state.selectedIdsForMarkAsPaid.length === 0) {
              return;
            }

            markExpenseAsPaidBulk({
              variables: {
                id: state.selectedIdsForMarkAsPaid,
                date: toApiDate(state.selectedDate.start),
              },
            });
          },
        }}
        selectedDate={state.selectedDate}
        title={t`Select paid date`}
        loading={markAsPaidLoading}
      />
    </React.Fragment>
  );
});
